import React, {
  createContext,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from 'react';
import gsap from 'gsap';
import {animateBackground} from '../animations/background-transition';
import {animateCards} from '../animations/cards-transition';
import {animateMirroredImage} from '../animations/mirrored-image-transition';
import {animateSliderImage} from '../animations/slider-image-transition';
import {animateCardsContent} from '../animations/cards-content-transition';

export type Theme = 'light' | 'dark';

export type AnimationRefs = {
  lightLayer?: HTMLElement | null;
  darkLayer?: HTMLElement | null;
  cardsWrapper?: HTMLElement | null;
  columnRef?: HTMLElement | null;
  topImg?: HTMLElement | null;
  bottomImg?: HTMLElement | null;
  sliderDarkImg?: HTMLElement | null;
  sliderLightImg?: HTMLElement | null;
  cardsContentDark?: HTMLElement[] | null;
  cardsContentLight?: HTMLElement[] | null;
};

export interface ThemeContextType {
  theme: Theme;
  isReversed: boolean;
  toggleTheme: () => void;
  registerRefs: (refs: Partial<AnimationRefs>) => void;
}

export const CustomThemeContext = createContext<ThemeContextType | undefined>(
  undefined,
);

export const CustomThemeProvider = ({children}: {children: ReactNode}) => {
  const [theme, setTheme] = useState<Theme>('dark');
  const [isReversed, setIsReversed] = useState(false);
  const refs = useRef<AnimationRefs>({});
  const isAnimating = useRef(false);

  const registerRefs = useCallback((newRefs: Partial<AnimationRefs>) => {
    refs.current = {...refs.current, ...newRefs};
  }, []);

  const toggleTheme = () => {
    // guard para que no se cree más de una timeline a la vez
    if (isAnimating.current) {
      return;
    }
    isAnimating.current = true;

    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    const nextReversed = !isReversed;
    const {
      lightLayer,
      darkLayer,
      cardsWrapper,
      columnRef,
      topImg,
      bottomImg,
      cardsContentDark,
      cardsContentLight,
    } = refs.current;

    // switch button updates immediately
    setIsReversed(nextReversed);

    const tl = gsap.timeline({
      onComplete: () => {
        setTheme(nextTheme);
        isAnimating.current = false;
      },
    });

    // mirrored image rotation (desktop)
    if (columnRef && topImg && bottomImg) {
      animateMirroredImage(tl, columnRef, topImg, bottomImg, nextReversed);
    }

    // slider image horizontal slide (mobile)
    const {sliderDarkImg, sliderLightImg} = refs.current;
    if (sliderDarkImg && sliderLightImg) {
      animateSliderImage(tl, sliderDarkImg, sliderLightImg, nextReversed);
    }

    // cards content crossfade
    if (cardsContentDark && cardsContentLight) {
      animateCardsContent(tl, cardsContentLight, cardsContentDark, nextTheme);
    }

    // cards move down and up (desktop)
    if (cardsWrapper) {
      animateCards(tl, cardsWrapper);
    }

    // background crossfade
    if (lightLayer && darkLayer) {
      animateBackground(tl, lightLayer, darkLayer, nextTheme);
    }
  };

  return (
    <CustomThemeContext.Provider
      value={{theme, toggleTheme, isReversed, registerRefs}}
    >
      {children}
    </CustomThemeContext.Provider>
  );
};
